import { useEffect, useRef, useState } from "react";

import { LinkButton, Text } from "@vericus/cadmus-ui";

import { NetworkStatus } from "@apollo/client";
import { useJitsu } from "@jitsu/react";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import {
  Notification,
  StudentActionFeedQuery,
  StudentActionFeedState,
} from "@/generated/graphql";
import { useRootNavigate } from "@/routes";

import { useSetupCharter } from "../hooks/use-setup-charter";
import { ActionCard } from "./action-card";
import * as styles from "./action-feed.css";
import { routeForAction } from "./action-routes";

export type StudentActionFeedPayload = NonNullable<
  StudentActionFeedQuery["studentActionFeed"]
>;
type WaitingNotePayload = StudentActionFeedPayload["waitingOnOthers"][number];

// The server ranks every open action and returns the full list. The schema
// doc on `StudentActionFeed.actions` says the client decides how many to show,
// so this cap is display-only and is never applied server-side.
const VISIBLE_CAP = 3;

// Stamped so a charter created from this feed reports the same
// navigation-source attribution that `routeForAction` already gives a charter
// the feed only routes to. `GroupCharterCard`'s own create CTA isn't reached
// from a notification, so it passes no marker and keeps navigating bare.
const CHARTER_SOURCE_MARKER = { source: "notification", sourceType: "charter" };

export interface ActionFeedProps {
  insights: StudentActionFeedPayload;
  /** Apollo status of the single lifted feed query; gates impression tracking. */
  networkStatus: NetworkStatus;
  /**
   * The student's group charter id, when one exists. Sourced from
   * `StudentHome`'s `useWorkGroupQuery` (`workGroup.charter.id`), the same id
   * `GroupCharterCard` navigates with, so a `charter` action item can
   * deep-link there instead of falling through to the safe `/home` fallback.
   * Undefined while that query is loading, or if the group has no charter yet.
   */
  charterId?: string | null;
  /**
   * The student's group id, from `workGroup.group.id`. Lets a `charter` action
   * with no `charterId` create the charter instead of routing to one that
   * doesn't exist yet.
   */
  groupId?: string;
  /**
   * Whether the work group is still being read or refreshed anywhere. A
   * `charter` action's create-vs-open choice reads `charterId` from that
   * document, so its card stays disabled until this settles rather than
   * acting on an id that hasn't arrived or a cached one about to be replaced.
   */
  workGroupPending?: boolean;
  /**
   * Whether group formation has finalised, from `workGroup.finalised`.
   * `GroupCharterCard` locks its own create CTA on this, so the feed's
   * charter action has to honour the same lock.
   */
  groupFinalised?: boolean | null;
}

/**
 * The left column of the new student home. It is presentational: every
 * string rendered here (`title`, `detail`, `action.label`, waiting-note
 * `title`/`detail`) comes verbatim from the server, with no client-authored
 * copy except inside `NothingToActOn`. `ActionCard` has no router access, so
 * this component owns routing (`routeForAction`) and click analytics.
 */
export function ActionFeed({
  insights,
  networkStatus,
  charterId,
  groupId,
  workGroupPending,
  groupFinalised,
}: ActionFeedProps) {
  const rootNavigate = useRootNavigate();
  const { track } = useJitsu();
  const { setupCharter, loading: settingUpCharter } = useSetupCharter(
    groupId,
    CHARTER_SOURCE_MARKER
  );
  const [expanded, setExpanded] = useState(false);
  const { state, actions, waitingOnOthers } = insights;

  const visibleActions = expanded ? actions : actions.slice(0, VISIBLE_CAP);
  const hiddenCount = actions.length - visibleActions.length;

  // One impression per painted action (the capped list, before any "+N more"
  // expand), plus the up-to-date empty state, credited once the single feed
  // query first settles — whether the network refresh succeeded (`ready`) or
  // failed (`error`). A warm cache paints real actions while
  // `cache-and-network` revalidates, and a failed revalidation settles into
  // `error` with those cached actions still on screen, so crediting only
  // `ready` would silently drop impressions the student actually saw. The
  // synthetic UNKNOWN fallback is excluded: a failed cold load renders the
  // degraded card with no real content to credit, and crediting it would burn
  // the ref before real content arrives on a later retry. The ref stops a
  // later refetch or StrictMode's double-invoke from re-crediting.
  const impressionTracked = useRef(false);
  const feedSettled =
    networkStatus === NetworkStatus.ready ||
    networkStatus === NetworkStatus.error;
  useEffect(() => {
    if (
      impressionTracked.current ||
      !feedSettled ||
      state === StudentActionFeedState.Unknown
    ) {
      return;
    }
    impressionTracked.current = true;

    actions.slice(0, VISIBLE_CAP).forEach((action) => {
      track(JitsuEvent.STUDENT_ACTION_SHOWN, {
        notification_type: action.type,
        tier: action.tier,
      });
    });

    // Only the genuine up-to-date state counts as empty; the
    // fact-family-unknown fallback is already excluded above.
    if (actions.length === 0 && state === StudentActionFeedState.UpToDate) {
      track(JitsuEvent.STUDENT_ACTIONS_EMPTY, {});
    }
  }, [feedSettled, actions, state, track]);

  // pantheon's create and review charter rules both emit rule_key :charter
  // (rules.ex), so the rule key never says which one fired. Everything that
  // does say lives in WorkGroup, which is why this resolves the action from
  // that query rather than from the notification. One function so the click
  // behaviour and the disabled check below can't drift apart.
  //
  // "locked" is charter creation before group formation finalises, the lock
  // GroupCharterCard puts on its own CTA. pantheon's create rule gates on
  // `group_finalised` too, so this is the client half of an agreement rather
  // than the only guard, and it keeps both callers of `useSetupCharter` in
  // step if that rule ever changes. Opening an existing charter is unaffected.
  //
  // "no-group" is separate because it is an anomaly, not a wait: a charter
  // action for a student with no group at all. That one still routes, so
  // `routeForAction` reports it to Sentry and lands on /home rather than
  // leaving a dead card and no trace of why.
  //
  // Order matters. "open" is settled before "waiting" so a known charter id
  // still routes mid-refresh, matching GroupCharterCard's view CTA: a refresh
  // can reveal a charter the cache missed, but it does not make one vanish.
  // Everything below "waiting" is a route to creating, which is the only
  // outcome a stale cache can get wrong.
  function charterState(action: Notification) {
    if (action.type !== "charter") return "not-charter";
    if (charterId != null) return "open";
    if (workGroupPending === true) return "waiting";
    if (groupId == null) return "no-group";
    if (groupFinalised !== true) return "locked";
    return "create";
  }

  function handleAct(action: Notification) {
    track(JitsuEvent.STUDENT_ACTION_CLICKED, {
      notification_type: action.type,
      tier: action.tier,
    });

    if (charterState(action) === "create") {
      setupCharter();
      return;
    }

    rootNavigate(routeForAction(action, charterId));
  }

  return (
    <div
      className={styles.feed}
      role="region"
      aria-label="What to do next"
      // Announces the swap between the action list, the "That's everything"
      // empty state, and the degraded "can't check everything" copy below;
      // without it none of those transitions reach a screen reader.
      aria-live="polite"
    >
      {(actions.length === 0 || state === StudentActionFeedState.Unknown) && (
        <NothingToActOn state={state} />
      )}

      {visibleActions.map((action, index) => {
        const charter = charterState(action);
        return (
          <ActionCard
            key={`${action.type}-${index}`}
            action={action}
            onAct={handleAct}
            disabled={
              charter === "waiting" ||
              charter === "locked" ||
              (charter === "create" && settingUpCharter)
            }
          />
        );
      })}

      {hiddenCount > 0 && (
        <LinkButton kind="primary" onClick={() => setExpanded(true)}>
          + {hiddenCount} more waiting on you
        </LinkButton>
      )}

      {waitingOnOthers.length > 0 && (
        <div className={styles.waitingList} data-testid="waiting-block">
          <Text kind="label" as="div" className={styles.waitingLabel}>
            Waiting on others
          </Text>
          {waitingOnOthers.map((note) => (
            <WaitingRow key={note.type} note={note} />
          ))}
        </div>
      )}
    </div>
  );
}

// The schema has no sentence for either empty-`actions` state, only `state`
// itself, so these two strings are client copy, not a rendering of anything
// the server sent. `UNKNOWN` gets the "can't check everything" copy
// regardless of `actions.length`, since the degraded state doesn't depend
// on how many actions still came back. A defensive `ACTIONS` payload that
// somehow arrives with an empty `actions` array reads as "up to date"
// instead, since zero actions to act on is a more trustworthy signal than
// a state label that contradicts it.
function NothingToActOn({ state }: { state: StudentActionFeedState }) {
  if (state === StudentActionFeedState.Unknown) {
    return (
      <div className={styles.emptyState}>
        <Text kind="bodyLine" bold>
          {"We can't check everything just now."}
        </Text>
        <Text kind="bodyLine">
          {
            "Some of your activity data isn't available. Try again shortly — we'd rather say so than show you a green tick we can't stand behind."
          }
        </Text>
      </div>
    );
  }

  return (
    <div className={styles.emptyState}>
      <Text kind="bodyLine">
        {"That's everything that needs you right now"}
      </Text>
    </div>
  );
}

function WaitingRow({ note }: { note: WaitingNotePayload }) {
  return (
    <div className={styles.waitingRow}>
      <Text kind="headingFive" color="link">
        {note.title}
      </Text>
      <Text kind="bodySm" color="link">
        {note.detail}
      </Text>
    </div>
  );
}
